import {
  computed,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  signal,
  ViewChild
} from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { parseISO, isValid } from 'date-fns';

import { Observable, of } from 'rxjs';
import {
  distinctUntilChanged, finalize,
  map,
  switchMap,
  takeUntil,
  catchError
} from 'rxjs/operators';

import { COMMON } from '@sondermind/constants';
import { USStateOptions } from '@sondermind/utilities/states-list';
import { ProviderNetworkCompany } from '@sondermind/utilities/models-client-facing';
import { BreakpointsService } from '@sondermind/utilities/services';
import { SharedMasks, TextMasks } from '@sondermind/utilities/text-masks';

import {
  PatientRelationshipDataService,
  PatientRelationship
} from '@sondermind/data-access/patient-relationships';
import { FEATURE_FLAG_SERVICE, IFeatureFlagService, FeatureFlagKey } from '@sondermind/data-access/feature-flags';
import {
  OTHER_INSURANCE_VALUE,
  InsuranceForm,
  ClientInsuranceHttpService,
  hasBenefitVerificationInfo
} from '@sondermind/data-access/client-insurance';
import { ProviderNetworkCompanyHttpService } from '@sondermind/data-access/provider-network-company';
import { scrollToFirstInvalidControl } from '@sondermind/iris/form-helpers';
import { IrisButtonContainerAlignments } from '@sondermind/iris/button';
import { IrisButtonTypeAttributeOptions } from '@sondermind/iris/shared';
import { DropdownOption } from '@sondermind/iris/dropdown';
import { BannerMessage } from '@sondermind/core/design/success-error-banner/success-error-banner.component';
import { BvErrorUIStore } from '../../stores/bv-error-ui.store';
import { PayerDataService } from '../../services/payer.service';
import { BvErrorData, BvErrorTypes } from '../bv-error/bv-error.types';

@Component({
  selector: 'client-insurance-add',
  templateUrl: './insurance-add.component.html',
  styleUrls: ['./insurance-add.component.scss'],
  standalone: false
})
export class InsuranceAddComponent implements OnInit, OnDestroy {
  @ViewChild('bvErrorBannerContainer') bvErrorBanner: ElementRef<HTMLElement>;
  // used for embedding form in new client account setup flow
  @Input() useInline: boolean = false;
  @Input() useInSessionRequest: boolean = false;
  @Input() showComponent: boolean = true;
  @Output() closeAdd = new EventEmitter<{
    insuranceUpdated: boolean;
    bvError: boolean;
    chcHasAnOutage: boolean;
    insuranceAddedId?: number;
    multiplePncSecondAttempt?: boolean;
    multiplePncFirstAttempt?: boolean;
    suggestedPayerRejected?: boolean;
    selfPaySelected?: boolean;
    regularBvSecondAttempt?: boolean;
    suggestedPayerBvFailure?: boolean;
    regularBvFirstAttempt?: boolean;
    noCoverageAccepted?: boolean;
    medicaidPlanRejected?: boolean;
    payorDowntimeAccepted?: boolean;
    demographicUpdated?: boolean;
    /** True when self-pay was chosen from a BV error modal (not the insurance vs self-pay radio). */
    selfPayAfterBvError?: boolean;
  }>();
  @Output() hideParentContent = new EventEmitter<boolean>();

  // used as part of the insurance-form.ts controls to toggle boolean of policy_holder
  // see the form controls for more details
  policyHolderSelf: number = -1;
  supportPhoneLink = COMMON.SUPPORT_PHONE_GENERAL_LINK;
  supportPhoneDisplay = COMMON.SUPPORT_PHONE_GENERAL_DISPLAY;
  chooseInsuranceState = OTHER_INSURANCE_VALUE;

  showAuthNumberField: boolean = false;
  submitting: boolean = false;
  processingNextStep: boolean = false;
  processingSelfPay: boolean = false;
  showCoverageMessage: boolean = false;
  readonly isTricarePayerSelected = signal<boolean>(false);
  readonly showTricareDependentCheckbox = signal<boolean>(false);
  chcHasAnOutageFeatureFlag: boolean = false;
  activeStates: string[] = [];
  hasAllStatesProvider: boolean = false;
  bvError: BannerMessage;

  suggestedPayer: ProviderNetworkCompany | null = null;
  showSuggestedPayerModal: boolean = false;
  suggestedPayerData: BvErrorData | null = null;
  originalInsuranceResponse: any = null;
  multiplePncHandled: boolean = false;

  showDemographicUpdateModal: boolean = false;
  demographicUpdateResponse: any = null;

  private readonly hasSuggestedPayorChange = signal<boolean>(false);
  private readonly hasOtherDemographicDifferences = signal<boolean>(false);

  isMobileOrTablet: boolean = false;
  masks: TextMasks = SharedMasks;
  dateToday: Date = new Date();
  maxDate: string = `${this.dateToday.getFullYear()}-${((this.dateToday.getMonth() + 1) < 10 ? '0' : '') + (this.dateToday.getMonth() + 1)}-${(this.dateToday.getDate() < 10 ? '0' : '') + this.dateToday.getDate()}`;

  payerList$: Observable<Array<DropdownOption<ProviderNetworkCompany>>>;
  private destroyed$ = new EventEmitter<void>();

  get checkingWithInsurance(): boolean {
    return this.submitting && !this.insuranceForm.failedBV;
  }

  /**
   * Insurance card images are required whenever the client is using insurance (not self-pay), matching
   * the form's `requiredWhenInsurance` validator. Surfacing this on the upload control renders the
   * required indicator and lets the field participate in the reveal-on-submit validation feedback.
   * Kept as a signal (driven by the `self_pay` control in `formInit`) so the template reads a value,
   * not a getter re-run on every change detection.
   */
  readonly cardImagesRequired = signal<boolean>(true);

  get isMobile(): boolean {
    return this.breakpointService.isMobile;
  }

  readonly memberIdLabel = computed(() => (
    this.isTricarePayerSelected()
      ? 'Benefits Number'
      : 'Member/ID Number'
  ));

  readonly memberIdHelperText = computed<string | null>(
    () => (this.isTricarePayerSelected()
      ? 'For TriCare, enter your Benefits Number from the back of your Military ID (example: 123456789-00).'
      : null)
  );

  IrisButtonContainerAlignments = IrisButtonContainerAlignments;
  IrisButtonTypeAttributeOptions = IrisButtonTypeAttributeOptions;
  usStateOption: Array<DropdownOption<string>> = [];
  relationshipListOption: Array<DropdownOption<number>> = [];
  statesLoading: boolean = true;

  constructor(
    private breakpointService: BreakpointsService,
    private payerDataService: PayerDataService,
    private relationshipDataService: PatientRelationshipDataService,
    public insuranceForm: InsuranceForm,
    private snackbar: MatSnackBar,
    @Inject(FEATURE_FLAG_SERVICE)
    private featureFlagService: IFeatureFlagService,
    private providerNetworkCompanyHttpService: ProviderNetworkCompanyHttpService,
    private bvErrorUIStore: BvErrorUIStore,
    private clientInsuranceHttpService: ClientInsuranceHttpService,
    private elementRef: ElementRef<HTMLElement>
  ) {}

  ngOnInit(): void {
    this.isMobileOrTablet = this.breakpointService.isMobileOrTablet;
    this.formInit();
    this.relationshipInit();
    this.payerInit();

    this.featureFlagService.isEnabled$(FeatureFlagKey.CHC_OUTAGE)
      .subscribe((CHC_OUTAGE) => {
        this.chcHasAnOutageFeatureFlag = CHC_OUTAGE;
      });
  }

  ngOnDestroy(): void {
    this.processingNextStep = false;
    this.destroyed$.next();
    this.destroyed$.complete();
    this.insuranceForm.destroy();
    this.bvErrorUIStore.clearPolicyNumber();
  }

  /**
   * Reveal Iris inline validation errors on every insurance control and scroll to the first invalid
   * one. Iris only renders errors on `invalid && touched`, so this is how the form tells the user
   * what is required after they try to advance. Returns whether the form is valid.
   */
  revealValidationErrors(): boolean {
    if (this.insuranceForm.form.valid) {
      return true;
    }
    this.insuranceForm.form.markAllAsTouched();
    scrollToFirstInvalidControl(this.elementRef.nativeElement);
    return false;
  }

  submit(): void {
    this.bvError = null;
    this.multiplePncHandled = false;
    this.startSubmitting();
    this.bvErrorUIStore.setPolicyNumber(this.insuranceForm.form.value.policy_number);

    this.insuranceForm.submit().pipe(
      finalize(() => {
        this.stopSubmitting();
      })
    ).subscribe({
      next: (resp) => {
        if (!hasBenefitVerificationInfo(resp)) {
          // `resp` is null when the client already had cash pay, so no record was created.
          if (!this.useInline && resp) {
            this.snackbar.open('Insurance record saved', 'Okay');
          }
          this.closeAdd.emit({
            insuranceUpdated: true,
            bvError: false,
            chcHasAnOutage: this.chcHasAnOutageFeatureFlag,
            insuranceAddedId: resp?.id
          });
          return;
        }

        const recentBv = resp.recentBenefitVerification;
        const coverageStatus = recentBv?.coverageStatus?.toLowerCase();
        const hasNoCoverage = coverageStatus === 'inactive' || coverageStatus === 'unknown';

        if (hasNoCoverage) {
          // Show no coverage BV error modal
          const bvErrorData = this.createBvErrorData(resp);
          bvErrorData.errorType = 'no_coverage';

          this.suggestedPayerData = bvErrorData;
          this.showSuggestedPayerModal = true;
          this.bvErrorUIStore.showBvErrorModal(true);
          this.hideParentContent.emit(true);
          this.originalInsuranceResponse = resp;
          return;
        }

        if (this.checkForVerificationDown(resp)) {
          return;
        }

        if (this.checkForMentalHealthNotCovered(resp)) {
          return;
        }

        if (this.checkForMedicaidPlan(resp)) {
          return;
        }

        if (this.checkForInsuranceOutOfNetwork(resp)) {
          return;
        }

        if (this.checkForUnknownCostshare(resp)) {
          return;
        }

        // Check for coverage date issues
        if (this.checkForCoverageNotStarted(resp)) {
          return;
        }

        if (this.checkForCoverageExpired(resp)) {
          return;
        }

        // Check if BV failed and has suggested payer
        if (recentBv?.error && recentBv.error.length > 0) {
          // Check for suggested payer before handling generic BV failure
          this.checkForSuggestedPayer(resp).subscribe(() => {
            if (this.suggestedPayer) {
              // Show the BV error component with suggested payer data
              const bvErrorData = this.createBvErrorData(resp);

              this.suggestedPayerData = bvErrorData;
              this.showSuggestedPayerModal = true;
              this.bvErrorUIStore.showBvErrorModal(true);
              this.hideParentContent.emit(true);
              this.originalInsuranceResponse = resp;
            } else if (!this.multiplePncHandled) {
              if (this.checkForDemographicUpdate(resp)) {
                return;
              }
              // No suggested payer found - handle as regular BV failure
              const bvFailedAttempts = this.insuranceForm.bvFailedId ? 2 : 1;
              this.insuranceForm.bvFailedId = resp?.id;
              this.handleBVFailure(bvFailedAttempts, resp?.id);
            }
          });
        } else {
          if (this.checkForDemographicUpdate(resp)) {
            return;
          }

          if (!this.useInline) {
            this.snackbar.open('Insurance record saved', 'Okay');
          }
          this.closeAdd.emit({
            insuranceUpdated: true,
            bvError: false,
            chcHasAnOutage: this.chcHasAnOutageFeatureFlag,
            insuranceAddedId: resp?.id
          });
        }
      },
      error: (err) => {
        this.stopSubmitting();

        if (this.chcHasAnOutageFeatureFlag) {
          this.closeAdd.emit({
            insuranceUpdated: true,
            bvError: true,
            insuranceAddedId: err?.bvFailedId,
            chcHasAnOutage: this.chcHasAnOutageFeatureFlag
          });
        } else if (err?.bvFailed) {
          // No snackbar here: the persistent "Unable to verify insurance" banner (shown by
          // handleBVFailure) is the primary, non-transient signal for a verification failure.
          this.insuranceForm.bvFailedId = err?.bvFailedId;
          this.handleBVFailure(err.bvFailed, err?.bvFailedId);
        } else {
          // Genuinely unexpected (non-BV) error — surface it so it isn't swallowed silently.
          this.snackbar.open('Error saving insurance', 'Okay');
        }
      }
    });
  }

  calculateSupplementalFields(selectedNetwork: ProviderNetworkCompany): void {
    this.isTricarePayerSelected.set(this.isTricareProviderNetwork(selectedNetwork));
    this.showTricareDependentCheckbox.set(this.isTricareActiveDutyNetwork(selectedNetwork));

    const isActiveDutyDependent = this.showTricareDependentCheckbox() && this.insuranceForm.form.get('is_tricare_dependent')?.value;
    const authNumberControl = this.insuranceForm.form.get('auth_number');

    if (!isActiveDutyDependent && selectedNetwork?.authNumberRequired) {
      this.showAuthNumberField = true;
      authNumberControl?.enable({ emitEvent: false });
      authNumberControl?.updateValueAndValidity();
    } else {
      this.showAuthNumberField = false;
      authNumberControl?.setValue(null);
      authNumberControl?.disable({ emitEvent: false });
      authNumberControl?.updateValueAndValidity();
    }
  }

  private isTricareProviderNetwork(selectedNetwork: ProviderNetworkCompany | null | undefined): boolean {
    const payerName = `${selectedNetwork?.name ?? ''} ${selectedNetwork?.externalTitle ?? ''}`.toLowerCase();
    return payerName.includes('tricare');
  }

  private isTricareActiveDutyNetwork(selectedNetwork: ProviderNetworkCompany | null | undefined): boolean {
    const payerName = `${selectedNetwork?.name ?? ''} ${selectedNetwork?.externalTitle ?? ''}`.toLowerCase();
    return payerName.includes('tricare prime active duty');
  }

  /**
   * Update list of PNCs when state selection changes.
   */
  insuranceStateChanged(state: string): void {
    const providerNetworkControl = this.insuranceForm.form.get('provider_network_company');

    this.showCoverageMessage = false;

    if (this.activeStates.includes(state) || this.hasAllStatesProvider) {
      providerNetworkControl.setValue(null);
      providerNetworkControl.enable();

      this.payerList$ = this.payerDataService.store$.pipe(
        map((ps) => ps
          .filter((p) => !p.eap && !p.isSnap && (p.networkState === state || p.clientsCanAddInAllStates))
          .map((payer) => ({
            value: payer,
            name: payer.externalTitle && payer.externalTitle.trim() !== '' ? payer.externalTitle : payer.name
          }))
        )
      );
    } else {
      providerNetworkControl.setValue(this.chooseInsuranceState);
      providerNetworkControl.disable();
    }

    this.insuranceForm.form.get('provider_network_company').valueChanges?.pipe(
      takeUntil(this.destroyed$)
    ).subscribe((value) => {
      this.insuranceChanged(value);
    });
  }

  /**
   * Show or hide Medicare text based on PNC selected.
   */
  insuranceChanged(ins: ProviderNetworkCompany): void {
    this.showCoverageMessage = ins?.medicareAdvantage;
  }

  formInit(): void {
    this.insuranceForm.initialize();
    this.insuranceForm.form.patchValue({
      self_pay: false
    });
    this.insuranceForm.enable();

    const selfPayControl = this.insuranceForm.form.get('self_pay');
    this.cardImagesRequired.set(!selfPayControl?.value);
    selfPayControl?.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((selfPay) => this.cardImagesRequired.set(!selfPay));

    this.processingSelfPay = false;
    this.processingNextStep = false;

    this.insuranceForm.form.get('insurance_state')?.disable();
    this.insuranceForm.form.get('provider_network_company')?.disable();

    this.insuranceForm.value$
      .pipe(
        map((value) => value.provider_network_company),
        distinctUntilChanged(),
        takeUntil(this.destroyed$)
      )
      .subscribe((pnc) => this.calculateSupplementalFields(pnc));

    this.insuranceForm.form.get('policy_holder_relationship_id')?.valueChanges?.pipe(
      takeUntil(this.destroyed$)
    ).subscribe(() => {
      Promise.resolve().then(() => this.formatDateForCurrentView());
    });

    this.insuranceForm.form.get('is_tricare_dependent')?.valueChanges?.pipe(
      takeUntil(this.destroyed$)
    ).subscribe(() => {
      const pnc = this.insuranceForm.form.get('provider_network_company')?.value;
      this.calculateSupplementalFields(pnc);
    });
  }

  private formatDateForCurrentView(): void {
    const dobControl = this.insuranceForm.form.get('date_of_birth');
    const currentValue = dobControl?.value;

    if (!currentValue || typeof currentValue !== 'string') {
      return;
    }

    if (currentValue.includes('-') && this.isMobileOrTablet) {
      const dateParts = currentValue.split('-');
      if (dateParts.length === 3) {
        const [year, month, day] = dateParts;
        const mobileFormat = `${month}/${day}/${year}`;
        dobControl.setValue(mobileFormat, { emitEvent: false });
      }
    }
  }

  payerInit(): void {
    const payersSort = (a: ProviderNetworkCompany, b: ProviderNetworkCompany) => a.name.localeCompare(b.name);

    this.statesLoading = true;

    this.payerDataService.refresh().pipe(
      switchMap(() => this.payerDataService.store$),
      map((ps) => {
        ps.sort(payersSort);

        const hasAllStatesProvider = ps.some((p) => !p.isSnap && p.clientsCanAddInAllStates);
        const stateSpecificNetworkStates = ps.filter((p) => !p.isSnap).map((n) => n.networkState);

        return { stateSpecificNetworkStates, hasAllStatesProvider };
      }),
      takeUntil(this.destroyed$)
    ).subscribe(({ stateSpecificNetworkStates, hasAllStatesProvider }) => {
      this.activeStates = stateSpecificNetworkStates.filter((p, i) => stateSpecificNetworkStates.indexOf(p) === i);
      this.hasAllStatesProvider = hasAllStatesProvider;

      if (this.activeStates.length) {
        this.insuranceStateChanged(this.insuranceForm.form.value.insurance_state);
      }

      if (hasAllStatesProvider) {
        this.usStateOption = USStateOptions.map((s) => ({
          value: s.value,
          name: s.label
        }));
      } else {
        this.usStateOption = USStateOptions.filter((s) => this.activeStates.includes(s.value)).map((s) => ({
          value: s.value,
          name: s.label
        }));
      }

      this.statesLoading = false;
      this.insuranceForm.form.get('insurance_state')?.enable();
      this.insuranceForm.form.get('provider_network_company')?.enable();
    });

    this.insuranceForm.form.get('insurance_state')?.valueChanges?.pipe(
      takeUntil(this.destroyed$)
    ).subscribe((value) => {
      this.insuranceStateChanged(value);
    });
  }

  relationshipInit(): void {
    const relationshipSort = (a: PatientRelationship, b: PatientRelationship) => a.type.localeCompare(b.type);

    this.relationshipDataService.refresh().pipe(
      switchMap(() => this.relationshipDataService.store$),
      map(
        (relList) => {
          relList.sort(relationshipSort);
          const relSelectList = relList.map((r) => ({
            id: r.id,
            label: this.getRelationshipLabel(r)
          }));
          relSelectList.unshift({ id: this.policyHolderSelf, label: `I'm the policyholder` });

          return relSelectList;
        }
      )
    ).subscribe(
      (result) => {
        this.relationshipListOption = result.map((r) => ({ value: r.id, name: r.label }));
      }
    );
  }

  getRelationshipLabel(rel: PatientRelationship): string {
    if (rel.id === this.policyHolderSelf) {
      return rel.type;
    } if (rel.type === 'Other Relationship') {
      return 'Other relationship';
    }
    return `My ${rel.type.toLowerCase().replace('child', 'parent')}`;
  }

  startSubmitting(): void {
    this.submitting = true;
  }

  stopSubmitting(): void {
    this.submitting = false;
  }

  handleBVFailure(bvFailedAttempts: number, bvFailedId?: number): void {
    if (bvFailedAttempts > 1) {
      this.closeAdd.emit({
        insuranceUpdated: true,
        bvError: true,
        regularBvSecondAttempt: true,
        insuranceAddedId: bvFailedId,
        chcHasAnOutage: this.chcHasAnOutageFeatureFlag
      });
    } else {
      this.bvError = {
        title: 'Unable to verify insurance',
        description: 'We weren\'t able to verify your insurance information. Please double check the form below and try again.'
      };

      // Re-enabling the form re-emits insurance_state's valueChanges, which clears the selected
      // provider via insuranceStateChanged() as if the state had changed. The state hasn't actually
      // changed here, so preserve and restore the provider — the user shouldn't have to re-pick their
      // insurance provider just to correct another field after a failed verification. Only a real
      // ProviderNetworkCompany (an object) is worth restoring; skip the `chooseInsuranceState` string
      // placeholder set when the state has no active payers.
      const providerControl = this.insuranceForm.form.get('provider_network_company');
      const selectedProvider = providerControl?.value;
      const hasRealProvider = typeof selectedProvider === 'object' && selectedProvider !== null;
      this.insuranceForm.enable();
      if (hasRealProvider && providerControl && !providerControl.value) {
        providerControl.setValue(selectedProvider);
      }

      this.scrollToError();

      // Emit closeAdd to stop button spinner, but don't trigger navigation
      this.closeAdd.emit({
        insuranceUpdated: false,
        bvError: false,
        regularBvFirstAttempt: true,
        insuranceAddedId: bvFailedId,
        chcHasAnOutage: this.chcHasAnOutageFeatureFlag
      });
    }
  }

  scrollToError(): void {
    // Defer to the next tick so the banner has rendered, then bring it into view within the modal's
    // scroll region. Targets the banner's in-flow wrapper (not an absolutely-positioned anchor, which
    // scrollIntoView can't locate reliably); centering keeps it visible under the sticky header.
    setTimeout(() => {
      this.bvErrorBanner?.nativeElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 0);
  }

  private checkForSuggestedPayer(resp: any): Observable<void> {
    if (resp.recentBenefitVerification?.error && resp.recentBenefitVerification.error.length > 0) {
      const suggestedPayerId = this.extractSuggestedPayerId(resp.recentBenefitVerification.error);

      if (suggestedPayerId) {
        const clientState = this.insuranceForm.form.value.insurance_state;

        // Set up filters - if payer ID is MABLS, don't include network_state filter
        const filters: Array<{ key: string; value: string; }> = [
          { key: 'nirvana_eligibility_id', value: suggestedPayerId }
        ];

        // Only add network_state filter if suggested payer ID is NOT "MABLS"
        if (suggestedPayerId !== 'MABLS') {
          filters.push({ key: 'network_state', value: clientState });
        }

        return this.providerNetworkCompanyHttpService.list({
          filter: filters
        }).pipe(
          switchMap((response) => {
            if (response.data.length !== 1) {
              this.suggestedPayer = null;

              const alreadyFailed = this.insuranceForm.bvFailedId != null;

              this.insuranceForm.bvFailedId = resp.id;

              this.multiplePncHandled = true;

              if (alreadyFailed) {
                this.handleBVFailure(2, resp?.id);
                this.closeAdd.emit({
                  insuranceUpdated: true,
                  bvError: false,
                  multiplePncSecondAttempt: true,
                  chcHasAnOutage: this.chcHasAnOutageFeatureFlag,
                  insuranceAddedId: resp?.id
                });
              } else {
                // First attempt - show banner
                this.handleBVFailure(1, resp?.id);
                this.closeAdd.emit({
                  insuranceUpdated: false,
                  bvError: false,
                  multiplePncFirstAttempt: true,
                  chcHasAnOutage: this.chcHasAnOutageFeatureFlag,
                  insuranceAddedId: resp?.id
                });
              }

              return of(undefined);
            }

            const suggestedPnc = response.data[0];
            this.suggestedPayer = suggestedPnc;
            return of(undefined);
          }),
          catchError((error) => {
            this.suggestedPayer = null;
            return of(undefined);
          })
        );
      }
    }

    this.suggestedPayer = null;
    return of(undefined);
  }

  private extractSuggestedPayerId(bvErrors: any[]): string | null {
    const errorWithSuggestedPayer = bvErrors.find((error) => {
      // Handle both string errors and object errors with message property
      const errorString = typeof error === 'string' ? error : error.message || '';

      return errorString.includes('suggested_payer ');
    });

    if (errorWithSuggestedPayer) {
      const errorString = typeof errorWithSuggestedPayer === 'string' ? errorWithSuggestedPayer : errorWithSuggestedPayer.message || '';
      const suggestedPayerMatch = errorString.match(/suggested_payer\s+([^,\s]+)/);

      if (suggestedPayerMatch) {
        const suggestedPayerId = suggestedPayerMatch[1];
        return suggestedPayerId;
      }
    }

    return null;
  }

  onAcceptSuggestedPayer(): void {
    this.processingNextStep = true;
    if (!this.suggestedPayer || !this.originalInsuranceResponse) {
      this.processingNextStep = false;
      this.snackbar.open('Error saving insurance', 'Okay');
      this.showSuggestedPayerModal = false;
      this.bvErrorUIStore.showBvErrorModal(false);
      this.hideParentContent.emit(false);
      return;
    }

    this.insuranceForm.form.patchValue({
      provider_network_company: this.suggestedPayer
    });

    this.showSuggestedPayerModal = false;
    this.bvErrorUIStore.showBvErrorModal(false);
    this.hideParentContent.emit(false);

    this.insuranceForm.bvFailedId = this.originalInsuranceResponse.id;

    this.bvErrorUIStore.setPolicyNumber(this.insuranceForm.form.value.policy_number);

    this.startSubmitting();

    this.insuranceForm.submit().pipe(
      finalize(() => this.stopSubmitting())
    ).subscribe({
      next: (resp) => {
        this.processingNextStep = false;
        if (!this.useInline) {
          this.snackbar.open('Insurance record saved', 'Okay');
        }

        const eventData = {
          insuranceUpdated: true,
          bvError: false,
          chcHasAnOutage: this.chcHasAnOutageFeatureFlag,
          insuranceAddedId: resp?.id
        };

        this.closeAdd.emit(eventData);
      },
      error: (err) => {
        this.processingNextStep = false;

        if (err?.bvFailed) {
          this.snackbar.open('Error saving insurance', 'Okay');
          this.insuranceForm.bvFailedId = err?.bvFailedId;

          // After accepting suggested payer, any BV failure should go to review screen
          this.closeAdd.emit({
            insuranceUpdated: true,
            bvError: true,
            suggestedPayerBvFailure: true,
            insuranceAddedId: err?.bvFailedId,
            chcHasAnOutage: this.chcHasAnOutageFeatureFlag
          });
        } else {
          this.snackbar.open('Error saving insurance', 'Okay');
        }
      }
    });
  }

  onRejectSuggestedPayer(): void {
    this.showSuggestedPayerModal = false;
    this.bvErrorUIStore.showBvErrorModal(false);
    this.hideParentContent.emit(false);

    this.startSubmitting();

    if (!this.originalInsuranceResponse) {
      this.snackbar.open('Error saving insurance', 'Okay');
      return;
    }

    const eventData = {
      insuranceUpdated: true,
      bvError: true,
      suggestedPayerRejected: true,
      chcHasAnOutage: this.chcHasAnOutageFeatureFlag,
      insuranceAddedId: this.originalInsuranceResponse?.id
    };

    this.closeAdd.emit(eventData);
  }

  private createBvErrorData(resp: any): BvErrorData {
    const demographicInfo = resp.recentBenefitVerification?.demographicInformation;
    const storedPolicyNumber = this.bvErrorUIStore.getPolicyNumber();
    const formValues = this.insuranceForm.form.value;

    let memberName: string;

    if (demographicInfo?.firstName && demographicInfo?.lastName) {
      memberName = `${demographicInfo.firstName} ${demographicInfo.lastName}`;
    } else {
      const firstName = resp.firstName || formValues.first_name || '';
      const lastName = resp.lastName || formValues.last_name || '';
      memberName = `${firstName} ${lastName}`.trim();
    }

    let dateOfBirth: string;

    if (demographicInfo?.dob) {
      dateOfBirth = demographicInfo.dob;
    } else if (resp.dateOfBirth) {
      dateOfBirth = resp.dateOfBirth;
    } else {
      dateOfBirth = formValues.date_of_birth;
    }

    let memberId: string;

    if (demographicInfo?.memberId) {
      memberId = demographicInfo.memberId;
    } else if (storedPolicyNumber) {
      memberId = storedPolicyNumber;
    } else {
      memberId = resp.policyNumber;
    }

    const result = {
      errorType: BvErrorTypes.SUGGESTED_PAYER, // This will be overridden by the caller
      suggestedPayer: this.suggestedPayer,
      memberName,
      dateOfBirth,
      memberId,
      groupNumber: formValues.group_number
    };

    return result;
  }

  private createCoverageNotStartedBvErrorData(resp: any): BvErrorData {
    const bv = resp?.recentBenefitVerification;

    return {
      errorType: BvErrorTypes.COVERAGE_NOT_STARTED,
      coverageStartDate: bv?.coverageStartDate
    };
  }

  onTertiaryAction(): void {
    if (this.suggestedPayerData?.errorType === BvErrorTypes.SUGGESTED_PAYER) {
      // Existing suggested payer logic
      this.showSuggestedPayerModal = false;
      this.bvErrorUIStore.showBvErrorModal(false);
      this.hideParentContent.emit(false);
      this.startSubmitting();

      if (!this.originalInsuranceResponse) {
        this.snackbar.open('Error saving insurance', 'Okay');
        return;
      }

      const eventData = {
        insuranceUpdated: true,
        bvError: true,
        suggestedPayerRejected: true,
        chcHasAnOutage: this.chcHasAnOutageFeatureFlag,
        insuranceAddedId: this.originalInsuranceResponse?.id
      };

      this.closeAdd.emit(eventData);
    } else if (this.suggestedPayerData?.errorType === BvErrorTypes.UNKNOWN_COSTSHARE) {
      // Tertiary action for unknown costshare: "Add a different insurance"
      this.showSuggestedPayerModal = false;
      this.bvErrorUIStore.showBvErrorModal(false);
      this.hideParentContent.emit(false);

      // Delete the existing insurance record first, then reset form
      if (this.originalInsuranceResponse?.id) {
        this.clientInsuranceHttpService.delete(this.originalInsuranceResponse.id).subscribe({
          next: () => {
            // Reset form and reinitialize to allow adding different insurance
            this.insuranceForm.form.reset();
            this.formInit();
            this.payerInit();
            this.originalInsuranceResponse = null;
          },
          error: () => {
            // Even if delete fails, reset form to allow adding different insurance
            this.insuranceForm.form.reset();
            this.formInit();
            this.payerInit();
            this.originalInsuranceResponse = null;
          }
        });
      } else {
        // No insurance record to delete, just reset form
        this.insuranceForm.form.reset();
        this.formInit();
        this.payerInit();
      }
    } else if (this.suggestedPayerData?.errorType === BvErrorTypes.COVERAGE_NOT_STARTED) {
      // Tertiary action: "Continue with this insurance" - keep insurance and navigate to payment
      this.onContinueWithInsurance();
    } else if (this.suggestedPayerData?.errorType === BvErrorTypes.COVERAGE_EXPIRED) {
      this.showSuggestedPayerModal = false;
      this.bvErrorUIStore.showBvErrorModal(false);
      this.hideParentContent.emit(false);

      const eventData = {
        insuranceUpdated: true,
        bvError: true,
        suggestedPayerRejected: true,
        chcHasAnOutage: this.chcHasAnOutageFeatureFlag,
        insuranceAddedId: this.originalInsuranceResponse?.id
      };
      this.closeAdd.emit(eventData);
    } else if (this.suggestedPayerData?.errorType === BvErrorTypes.NO_COVERAGE ||
               this.suggestedPayerData?.errorType === BvErrorTypes.MENTAL_HEALTH_NOT_COVERED) {
      this.onContinueWithInsurance();
    }
  }

  onPrimaryAction(): void {
    if (this.suggestedPayerData?.errorType === BvErrorTypes.SUGGESTED_PAYER) {
      this.onAcceptSuggestedPayer();
    } else if (this.suggestedPayerData?.errorType === BvErrorTypes.PAYOR_DOWNTIME) {
      this.showSuggestedPayerModal = false;
      this.bvErrorUIStore.showBvErrorModal(false);
      this.hideParentContent.emit(false);

      if (!this.useInline) {
        this.snackbar.open('Insurance record saved', 'Okay');
      }

      this.closeAdd.emit({
        insuranceUpdated: true,
        bvError: false,
        payorDowntimeAccepted: true,
        chcHasAnOutage: this.chcHasAnOutageFeatureFlag,
        insuranceAddedId: this.originalInsuranceResponse?.id
      });
    } else if (this.suggestedPayerData?.errorType === BvErrorTypes.UNKNOWN_COSTSHARE) {
      // Primary action for unknown costshare: "Continue with this insurance"
      this.showSuggestedPayerModal = false;
      this.bvErrorUIStore.showBvErrorModal(false);
      this.hideParentContent.emit(false);

      if (!this.useInline) {
        this.snackbar.open('Insurance record saved', 'Okay');
      }

      this.closeAdd.emit({
        insuranceUpdated: true,
        bvError: false,
        chcHasAnOutage: this.chcHasAnOutageFeatureFlag,
        insuranceAddedId: this.originalInsuranceResponse?.id
      });
    } else if (this.suggestedPayerData?.errorType === BvErrorTypes.MEDICAID_PLAN) {
      this.showSuggestedPayerModal = false;
      this.bvErrorUIStore.showBvErrorModal(false);
      this.hideParentContent.emit(false);

      if (this.originalInsuranceResponse?.id) {
        this.clientInsuranceHttpService.delete(this.originalInsuranceResponse.id).subscribe({
          next: () => {
            this.closeAdd.emit({
              insuranceUpdated: false,
              bvError: false,
              chcHasAnOutage: this.chcHasAnOutageFeatureFlag,
              medicaidPlanRejected: true
            });
          },
          error: () => {
            this.closeAdd.emit({
              insuranceUpdated: false,
              bvError: false,
              chcHasAnOutage: this.chcHasAnOutageFeatureFlag,
              medicaidPlanRejected: true
            });
          }
        });
      } else {
        this.closeAdd.emit({
          insuranceUpdated: false,
          bvError: false,
          chcHasAnOutage: this.chcHasAnOutageFeatureFlag,
          medicaidPlanRejected: true
        });
      }
    } else if (this.suggestedPayerData?.errorType === BvErrorTypes.COVERAGE_NOT_STARTED) {
      // Primary action: "Add a different insurance" - delete existing insurance and reset form
      this.showSuggestedPayerModal = false;
      this.bvErrorUIStore.showBvErrorModal(false);
      this.hideParentContent.emit(false);

      // Delete the existing insurance record first, then reset form
      if (this.originalInsuranceResponse?.id) {
        this.clientInsuranceHttpService.delete(this.originalInsuranceResponse.id).subscribe({
          next: () => {
            // Reset form and reinitialize to allow adding different insurance
            this.insuranceForm.form.reset();
            this.formInit();
            this.payerInit();
            this.originalInsuranceResponse = null;
          },
          error: () => {
            // Even if delete fails, reset form to allow adding different insurance
            this.insuranceForm.form.reset();
            this.formInit();
            this.payerInit();
            this.originalInsuranceResponse = null;
          }
        });
      } else {
        // No insurance record to delete, just reset form
        this.insuranceForm.form.reset();
        this.formInit();
        this.payerInit();
      }
    } else if (this.suggestedPayerData?.errorType === BvErrorTypes.COVERAGE_EXPIRED) {
      // Primary action: "Add a different insurance" - delete existing insurance and reset form
      this.showSuggestedPayerModal = false;
      this.bvErrorUIStore.showBvErrorModal(false);
      this.hideParentContent.emit(false);

      // Delete the existing insurance record first, then reset form
      if (this.originalInsuranceResponse?.id) {
        this.clientInsuranceHttpService.delete(this.originalInsuranceResponse.id).subscribe({
          next: () => {
            // Reset form and reinitialize to allow adding different insurance
            this.insuranceForm.form.reset();
            this.formInit();
            this.payerInit();
            this.originalInsuranceResponse = null;
          },
          error: () => {
            // Even if delete fails, reset form to allow adding different insurance
            this.insuranceForm.form.reset();
            this.formInit();
            this.payerInit();
            this.originalInsuranceResponse = null;
          }
        });
      } else {
        // No insurance record to delete, just reset form
        this.insuranceForm.form.reset();
        this.formInit();
        this.payerInit();
      }
    } else if (this.suggestedPayerData?.errorType === BvErrorTypes.NO_COVERAGE ||
               this.suggestedPayerData?.errorType === BvErrorTypes.MENTAL_HEALTH_NOT_COVERED) {
      this.onAddDifferentInsurance();
    } else if (this.suggestedPayerData?.errorType === BvErrorTypes.INSURANCE_OUT_OF_NETWORK) {
      // Primary action: "Continue with self-pay" - delete insurance and add cash pay
      this.onSelfPaySelected();
    }
  }

  onSecondaryAction(): void {
    if (this.suggestedPayerData?.errorType === BvErrorTypes.SUGGESTED_PAYER) {
      this.onRejectSuggestedPayer();
    } else if (this.suggestedPayerData?.errorType === BvErrorTypes.UNKNOWN_COSTSHARE) {
      this.onSelfPaySelected();
    } else if (this.suggestedPayerData?.errorType === BvErrorTypes.COVERAGE_NOT_STARTED) {
      this.onSelfPaySelected();
    } else if (this.suggestedPayerData?.errorType === BvErrorTypes.COVERAGE_EXPIRED) {
      this.onSelfPaySelected();
    } else if (this.suggestedPayerData?.errorType === BvErrorTypes.NO_COVERAGE ||
               this.suggestedPayerData?.errorType === BvErrorTypes.MENTAL_HEALTH_NOT_COVERED) {
      this.onSelfPaySelected();
    } else if (this.suggestedPayerData?.errorType === BvErrorTypes.INSURANCE_OUT_OF_NETWORK) {
      // Secondary action: "Close" - delete insurance and exit the flow
      this.showSuggestedPayerModal = false;
      this.bvErrorUIStore.showBvErrorModal(false);
      this.hideParentContent.emit(false);

      const closeEventData = {
        insuranceUpdated: false,
        bvError: false,
        chcHasAnOutage: this.chcHasAnOutageFeatureFlag
      };

      if (this.originalInsuranceResponse?.id) {
        this.clientInsuranceHttpService.delete(this.originalInsuranceResponse.id).subscribe({
          next: () => {
            this.closeAdd.emit(closeEventData);
          },
          error: () => {
            this.closeAdd.emit(closeEventData);
          }
        });
      } else {
        this.closeAdd.emit(closeEventData);
      }
    }
  }

  onSelfPaySelected(): void {
    this.processingNextStep = true;
    this.showSuggestedPayerModal = false;
    this.bvErrorUIStore.showBvErrorModal(false);
    this.hideParentContent.emit(false);
    this.processingSelfPay = true;
    if (this.originalInsuranceResponse?.id) {
      this.clientInsuranceHttpService.delete(this.originalInsuranceResponse.id).pipe(
        switchMap(() => this.handleAddCashPay()),
        finalize(() => {
          this.processingNextStep = false;
        })
      ).subscribe({
        next: () => {
          this.processingSelfPay = false;
          this.closeAdd.emit({
            insuranceUpdated: false,
            bvError: false,
            selfPaySelected: true,
            selfPayAfterBvError: true,
            chcHasAnOutage: this.chcHasAnOutageFeatureFlag
          });
        },
        error: (error) => {
          this.processingSelfPay = false;
          this.closeAdd.emit({
            insuranceUpdated: false,
            bvError: false,
            selfPaySelected: true,
            selfPayAfterBvError: true,
            chcHasAnOutage: this.chcHasAnOutageFeatureFlag
          });
        }
      });
    } else {
      this.handleAddCashPay().pipe(
        finalize(() => {
          this.processingNextStep = false;
        })
      ).subscribe({
        next: () => {
          this.processingSelfPay = false;
          this.closeAdd.emit({
            insuranceUpdated: false,
            bvError: false,
            selfPaySelected: true,
            selfPayAfterBvError: true,
            chcHasAnOutage: this.chcHasAnOutageFeatureFlag
          });
        },
        error: (error) => {
          this.processingSelfPay = false;
          this.closeAdd.emit({
            insuranceUpdated: false,
            bvError: false,
            selfPaySelected: true,
            selfPayAfterBvError: true,
            chcHasAnOutage: this.chcHasAnOutageFeatureFlag
          });
        }
      });
    }
  }

  onAddDifferentInsurance(): void {
    this.showSuggestedPayerModal = false;
    this.bvErrorUIStore.showBvErrorModal(false);
    this.hideParentContent.emit(false);

    if (this.originalInsuranceResponse?.id) {
      this.clientInsuranceHttpService.delete(this.originalInsuranceResponse.id).subscribe({
        next: () => {
          this.insuranceForm.form.reset();
          this.formInit();
          this.payerInit();
          this.originalInsuranceResponse = null;
        },
        error: () => {
          this.insuranceForm.form.reset();
          this.formInit();
          this.payerInit();
          this.originalInsuranceResponse = null;
        }
      });
    } else {
      this.insuranceForm.form.reset();
      this.formInit();
      this.payerInit();
    }
  }

  onContinueWithInsurance(): void {
    this.showSuggestedPayerModal = false;
    this.bvErrorUIStore.showBvErrorModal(false);
    this.hideParentContent.emit(false);

    if (!this.useInline) {
      this.snackbar.open('Insurance record saved', 'Okay');
    }

    this.closeAdd.emit({
      insuranceUpdated: true,
      bvError: true,
      noCoverageAccepted: true, // Specific flag for no coverage acceptance
      chcHasAnOutage: this.chcHasAnOutageFeatureFlag,
      insuranceAddedId: this.originalInsuranceResponse?.id
    });
  }

  private handleAddCashPay(): Observable<any> {
    return this.clientInsuranceHttpService.addCashPay().pipe(
      catchError((err) => {
        this.snackbar.open('Error adding cash pay option', 'Okay');
        throw err;
      })
    );
  }

  onDemographicUpdateClosed(event: any): void {
    this.showDemographicUpdateModal = false;
    this.bvErrorUIStore.showBvErrorModal(false);
    this.hideParentContent.emit(false);

    if (event.demographicUpdated) {
      if (!this.useInline) {
        this.snackbar.open('Insurance information updated', 'Okay');
      }

      this.closeAdd.emit({
        insuranceUpdated: true,
        bvError: false,
        chcHasAnOutage: this.chcHasAnOutageFeatureFlag,
        insuranceAddedId: event.insuranceAddedId,
        demographicUpdated: true
      });
    } else {
      this.closeAdd.emit({
        insuranceUpdated: event.insuranceUpdated,
        bvError: event.bvError,
        chcHasAnOutage: this.chcHasAnOutageFeatureFlag,
        insuranceAddedId: event.insuranceAddedId,
        demographicUpdated: false
      });
    }
  }

  private checkForUnknownCostshare(resp: any): boolean {
    const bv = resp?.recentBenefitVerification;

    if (bv?.error && bv.error.length > 0) {
      const hasUnknownCostshare = bv.error.some((error: any) => {
        const errorString = typeof error === 'string' ? error : error.message || '';
        return errorString.startsWith('UNKNOWN_COSTSHARE');
      });

      if (hasUnknownCostshare) {
        const bvErrorData = this.createUnknownCostshareBvErrorData(resp);

        this.suggestedPayerData = bvErrorData;
        this.showSuggestedPayerModal = true;
        this.bvErrorUIStore.showBvErrorModal(true);
        this.hideParentContent.emit(true);
        this.originalInsuranceResponse = resp;

        return true;
      }
    }

    return false;
  }

  private checkForCoverageNotStarted(resp: any): boolean {
    const bv = resp?.recentBenefitVerification;
    const coverageStartDate = bv?.coverageStartDate;

    if (coverageStartDate) {
      const startDate = new Date(coverageStartDate);
      const today = new Date();

      today.setHours(0, 0, 0, 0);
      startDate.setHours(0, 0, 0, 0);

      if (startDate > today) {
        const bvErrorData = this.createCoverageNotStartedBvErrorData(resp);

        this.suggestedPayerData = bvErrorData;
        this.showSuggestedPayerModal = true;
        this.bvErrorUIStore.showBvErrorModal(true);
        this.hideParentContent.emit(true);
        this.originalInsuranceResponse = resp;

        return true;
      }
    }

    return false;
  }

  private checkForCoverageExpired(resp: any): boolean {
    const bv = resp?.recentBenefitVerification;
    const coverageEndDate = bv?.coverageEndDate;

    if (coverageEndDate) {
      const endDate = new Date(coverageEndDate);
      const today = new Date();

      today.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);

      if (endDate < today) {
        const bvErrorData = this.createCoverageExpiredBvErrorData(resp);

        this.suggestedPayerData = bvErrorData;
        this.showSuggestedPayerModal = true;
        this.bvErrorUIStore.showBvErrorModal(true);
        this.hideParentContent.emit(true);
        this.originalInsuranceResponse = resp;

        return true;
      }
    }

    return false;
  }

  private createUnknownCostshareBvErrorData(resp: any): BvErrorData {
    return {
      errorType: BvErrorTypes.UNKNOWN_COSTSHARE
    };
  }

  private checkForVerificationDown(resp: any): boolean {
    const bv = resp?.recentBenefitVerification;
    const failureReason = bv?.failureReason;

    if (failureReason === BvErrorTypes.PAYOR_DOWNTIME) {
      const bvErrorData = this.createVerificationDownBvErrorData(resp);

      this.suggestedPayerData = bvErrorData;
      this.showSuggestedPayerModal = true;
      this.bvErrorUIStore.showBvErrorModal(true);
      this.hideParentContent.emit(true);
      this.originalInsuranceResponse = resp;

      return true;
    }

    return false;
  }

  private createVerificationDownBvErrorData(resp: any): BvErrorData {
    return {
      errorType: BvErrorTypes.PAYOR_DOWNTIME,
      failureReason: resp?.recentBenefitVerification?.failureReason
    };
  }

  private createCoverageExpiredBvErrorData(resp: any): BvErrorData {
    const bv = resp?.recentBenefitVerification;

    return {
      errorType: BvErrorTypes.COVERAGE_EXPIRED,
      coverageEndDate: bv?.coverageEndDate
    };
  }

  private checkForMentalHealthNotCovered(resp: any): boolean {
    const bv = resp?.recentBenefitVerification;
    const failureReason = bv?.failureReason;

    if (failureReason === BvErrorTypes.MENTAL_HEALTH_NOT_COVERED) {
      const bvErrorData = {
        errorType: BvErrorTypes.MENTAL_HEALTH_NOT_COVERED
      };

      this.suggestedPayerData = bvErrorData;
      this.showSuggestedPayerModal = true;
      this.bvErrorUIStore.showBvErrorModal(true);
      this.hideParentContent.emit(true);
      this.originalInsuranceResponse = resp;

      return true;
    }

    return false;
  }

  private checkForMedicaidPlan(resp: any): boolean {
    const bv = resp?.recentBenefitVerification;
    const failureReason = bv?.failureReason;

    if (failureReason === BvErrorTypes.MEDICAID_PLAN) {
      const bvErrorData = {
        errorType: BvErrorTypes.MEDICAID_PLAN
      };

      this.suggestedPayerData = bvErrorData;
      this.showSuggestedPayerModal = true;
      this.bvErrorUIStore.showBvErrorModal(true);
      this.hideParentContent.emit(true);
      this.originalInsuranceResponse = resp;

      return true;
    }

    return false;
  }

  private checkForInsuranceOutOfNetwork(resp: any): boolean {
    const bv = resp?.recentBenefitVerification;
    const failureReason = bv?.failureReason;

    if (failureReason === BvErrorTypes.INSURANCE_OUT_OF_NETWORK) {
      const insuranceName = this.insuranceForm.form.value.provider_network_company?.externalTitle
        || this.insuranceForm.form.value.provider_network_company?.name
        || 'your insurance';

      const bvErrorData = {
        errorType: BvErrorTypes.INSURANCE_OUT_OF_NETWORK,
        insuranceName
      };

      this.suggestedPayerData = bvErrorData;
      this.showSuggestedPayerModal = true;
      this.bvErrorUIStore.showBvErrorModal(true);
      this.hideParentContent.emit(true);
      this.originalInsuranceResponse = resp;

      return true;
    }

    return false;
  }

  private checkForDemographicUpdate(resp: any): boolean {
    const bv = resp?.recentBenefitVerification;
    const demographicInfo = bv?.demographicInformation;

    if (demographicInfo) {
      const formValues = this.insuranceForm.form.value;
      const hasDifferences = this.hasDemographicDifferences(formValues, resp, demographicInfo);

      if (hasDifferences) {
        if (this.hasSuggestedPayorChange() && this.hasOtherDemographicDifferences()) {
          // Both payor change and other demographic differences exist
          // Update payor first, then show modal for other differences
          const updateData: any = {
            skip_benefit_verification: true,
            provider_network_company_id: bv?.suggestedPayer?.id
          };

          this.clientInsuranceHttpService.update(resp.id, updateData)
            .pipe(
              takeUntil(this.destroyed$),
              catchError(() => of(resp))
            )
            .subscribe({
              next: (updatedResp) => {
                // After payor update completes, show modal for remaining demographic differences
                this.demographicUpdateResponse = updatedResp;
                this.showDemographicUpdateModal = true;
                this.bvErrorUIStore.showBvErrorModal(true);
                this.hideParentContent.emit(true);
              },
              error: () => {
                // On error, still show the modal so user can update everything manually
                this.demographicUpdateResponse = resp;
                this.showDemographicUpdateModal = true;
                this.bvErrorUIStore.showBvErrorModal(true);
                this.hideParentContent.emit(true);
              }
            });
          return true;
        }

        if (this.hasSuggestedPayorChange()) {
          // Only payor change - automatically update without showing UI
          this.updateInsuranceWithSuggestedChanges(resp, demographicInfo);
          return true;
        }

        // Show modal for other demographic differences
        this.demographicUpdateResponse = resp;
        this.showDemographicUpdateModal = true;
        this.bvErrorUIStore.showBvErrorModal(true);
        this.hideParentContent.emit(true);
        return true;
      }
    }

    return false;
  }

  private updateInsuranceWithSuggestedChanges(resp: any, demographicInfo: any): void {
    if (!resp?.id) {
      this.snackbar.open('Error updating insurance information', 'Okay');
      return;
    }

    const bv = resp?.recentBenefitVerification;
    const suggestedPayer = bv?.suggestedPayer;
    const formValues = this.insuranceForm.form.value;

    // Build update data matching the demographic-update component logic
    const updateData: any = {
      skip_benefit_verification: true,
      provider_network_company_id: suggestedPayer?.id || formValues.provider_network_company?.id,
      group_number: resp?.groupNumber || resp?.insuranceInformation?.groupNumber,
      policy_number: demographicInfo?.policyNumber || demographicInfo?.memberId,
      first_name: demographicInfo?.firstName,
      last_name: demographicInfo?.lastName,
      date_of_birth: demographicInfo?.dateOfBirth || demographicInfo?.dob,
      gender: demographicInfo?.gender,
      address_line1: demographicInfo?.addressLine1,
      address_line2: demographicInfo?.addressLine2,
      city: demographicInfo?.city,
      state: demographicInfo?.state,
      postal_code: demographicInfo?.postalCode
    };

    this.clientInsuranceHttpService.update(resp.id, updateData)
      .pipe(
        takeUntil(this.destroyed$)
      )
      .subscribe({
        next: (updatedResp) => {
          if (!this.useInline) {
            this.snackbar.open('Insurance record saved', 'Okay');
          }

          this.closeAdd.emit({
            insuranceUpdated: true,
            bvError: false,
            chcHasAnOutage: this.chcHasAnOutageFeatureFlag,
            insuranceAddedId: updatedResp?.id,
            demographicUpdated: true
          });
        },
        error: (err) => {
          this.snackbar.open('Error updating insurance information', 'Okay');

          // Even on error, emit to allow user to continue
          this.closeAdd.emit({
            insuranceUpdated: false,
            bvError: true,
            chcHasAnOutage: this.chcHasAnOutageFeatureFlag,
            insuranceAddedId: resp?.id,
            demographicUpdated: false
          });
        }
      });
  }

  private hasDemographicDifferences(formValues: any, resp: any, demographicInfo: any): boolean {
    // Reset signals
    this.hasSuggestedPayorChange.set(false);
    this.hasOtherDemographicDifferences.set(false);

    let hasNonPayorDiffs = false;

    // Check non-payor demographic differences
    const userFirstName = formValues.first_name?.trim()?.toLowerCase();
    const payorFirstName = demographicInfo.firstName?.trim()?.toLowerCase();
    if (userFirstName && payorFirstName && userFirstName !== payorFirstName) {
      hasNonPayorDiffs = true;
    }

    const userLastName = formValues.last_name?.trim()?.toLowerCase();
    const payorLastName = demographicInfo.lastName?.trim()?.toLowerCase();
    if (userLastName && payorLastName && userLastName !== payorLastName) {
      hasNonPayorDiffs = true;
    }

    const userDob = formValues.date_of_birth;
    const payorDob = demographicInfo.dateOfBirth || demographicInfo.dob;
    if (userDob && payorDob && !this.datesMatch(userDob, payorDob)) {
      hasNonPayorDiffs = true;
    }

    const userPolicyNumber = formValues.policy_number?.trim();
    const payorPolicyNumber = demographicInfo.policyNumber || demographicInfo.memberId;
    if (userPolicyNumber && payorPolicyNumber && userPolicyNumber !== payorPolicyNumber) {
      hasNonPayorDiffs = true;
    }

    // Check for suggested payor change
    const bv = resp?.recentBenefitVerification;
    const suggestedPayerId = bv?.suggestedPayer?.id;
    const userInsuranceId = formValues.provider_network_company?.id;
    const hasPayorChange = suggestedPayerId && userInsuranceId && suggestedPayerId !== userInsuranceId;

    // Set signals
    this.hasSuggestedPayorChange.set(!!hasPayorChange);
    this.hasOtherDemographicDifferences.set(hasNonPayorDiffs);

    return hasNonPayorDiffs || !!hasPayorChange;
  }

  private datesMatch(userDate: string, payorDate: string): boolean {
    try {
      let userParsed: Date;
      let payorParsed: Date;

      if (userDate.includes('/')) {
        const [month, day, year] = userDate.split('/');
        const isoString = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        userParsed = parseISO(isoString);
      } else {
        userParsed = parseISO(userDate);
      }

      if (payorDate.includes('/')) {
        const [month, day, year] = payorDate.split('/');
        const isoString = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        payorParsed = parseISO(isoString);
      } else {
        payorParsed = parseISO(payorDate);
      }

      if (isValid(userParsed) && isValid(payorParsed)) {
        return userParsed.getTime() === payorParsed.getTime();
      }

      return userDate === payorDate;
    } catch (error) {
      return userDate === payorDate;
    }
  }
}
